import React from 'react';
import { Paper, Stack, Select, Group, MultiSelect } from '@mantine/core';
import { CalendarDateInput } from '../../../global/component/molecules/CalendarDateInput';
import { IconFilter, IconCalendar, IconBuildingHospital } from '@tabler/icons-react';
import { IBillingReportFilter, BillingReportFilter } from '../../../global/entities/TestKitOrder/BillingTypes';
import { useGetHealthcareFacilities } from '../../../global/entities/healthcareFacility/queries/useGetHealthcareFacilities';
import { useTranslation } from 'react-i18next';

/**
 * BillingFilters - Filter component for billing management
 * Part of BL-002 Admin Billing Reports Feature - Steps 21-33
 */
interface BillingFiltersProps {
    onFiltersChange?: (filters: IBillingReportFilter) => void;
    isLoading?: boolean;
}

interface FilterState {
    batchStatus: string | null; // Step 45: Batch filtering
    orderStatus: string | null; // Order status filtering (Draft, Sent, Billed)
    fromDate: Date | null;
    toDate: Date | null;
    facilities: string[];
}

const BillingFilters: React.FC<BillingFiltersProps> = ({ onFiltersChange, isLoading = false }) => {
    const { t } = useTranslation();
    
    const [filters, setFilters] = React.useState<FilterState>({
        batchStatus: 'not-batched', // Step 45: Default batch filter - not in batch
        orderStatus: 'all', // Default to show all order statuses
        fromDate: null,
        toDate: null,
        facilities: [],
    });

    // Fetch healthcare facilities data
    const { data: healthcareFacilities, isLoading: facilitiesLoading, error: facilitiesError } = useGetHealthcareFacilities();

    // Convert UI filter state to API filter format
    const convertToApiFilter = (uiFilters: FilterState): IBillingReportFilter => {
        // Map batch status to proper backend status filter values
        const batchStatusMap: Record<string, number> = {
            'all': 2, // All orders (no filtering)
            'batched': 4, // Orders in batch (BillingBatchID IS NOT NULL)
            'not-batched': 5, // Orders not in batch (BillingBatchID IS NULL)
        };

        // Map order status to backend OrderStatusID values
        const orderStatusMap: Record<string, number> = {
            'all': 0, // All order statuses (no filtering)
            'draft': 1, // Draft orders (OrderStatusID = 1)
            'sent': 2, // Sent orders (OrderStatusID = 2)
            'billed': 3, // Billed orders (OrderStatusID = 3)
        };

        return new BillingReportFilter({
            statusFilter: uiFilters.batchStatus ? batchStatusMap[uiFilters.batchStatus] : 2,
            orderStatusFilter: uiFilters.orderStatus ? orderStatusMap[uiFilters.orderStatus] : 0,
            startDate: uiFilters.fromDate,
            endDate: uiFilters.toDate,
            healthcareFacilityIDs: uiFilters.facilities.length > 0 
                ? uiFilters.facilities.map(id => parseInt(id, 10))
                : null,
            pageNumber: 1, // Start with first page when filters change
            pageSize: 25, // Default page size
            sortBy: 'CreatedDate',
            sortDirection: 'DESC',
        });
    };

    // Track if initial filter has been applied
    const initialFilterApplied = React.useRef(false);

    // Apply initial filters on component mount
    React.useEffect(() => {
        if (!initialFilterApplied.current && onFiltersChange) {
            const apiFilter = convertToApiFilter(filters);
            onFiltersChange(apiFilter);
            initialFilterApplied.current = true;
        }
    }, [filters, onFiltersChange]);

    // Step 45: Batch status options for filtering
    const batchStatusOptions = [
        { value: 'all', label: t('billing.filters.batchStatus.all') },
        { value: 'batched', label: t('billing.filters.batchStatus.batched') },
        { value: 'not-batched', label: t('billing.filters.batchStatus.notBatched') },
    ];

    // Order status options for filtering
    const orderStatusOptions = [
        { value: 'all', label: t('billing.filters.orderStatus.all') },
        { value: 'draft', label: t('billing.filters.orderStatus.draft') },
        { value: 'sent', label: t('billing.filters.orderStatus.sent') },
        { value: 'billed', label: t('billing.filters.orderStatus.billed') },
    ];

    const updateFilters = (updates: Partial<FilterState>) => {
        const newFilters = { ...filters, ...updates };
        setFilters(newFilters);
        
        // Convert to API format and trigger callback
        const apiFilter = convertToApiFilter(newFilters);
        onFiltersChange?.(apiFilter);
    };

    // Step 45: Batch status change handler
    const handleBatchStatusChange = (value: string | null) => {
        updateFilters({ batchStatus: value });
    };

    // Order status change handler
    const handleOrderStatusChange = (value: string | null) => {
        updateFilters({ orderStatus: value });
    };

    const handleFromDateChange = (date: Date | null) => {
        updateFilters({ fromDate: date });
    };

    const handleToDateChange = (date: Date | null) => {
        updateFilters({ toDate: date });
    };

    const handleFacilitiesChange = (facilities: string[]) => {
        updateFilters({ facilities });
    };

    // Transform healthcare facilities data for MultiSelect component
    const facilityOptions = React.useMemo(() => {
        if (!healthcareFacilities || facilitiesError) 
            return [];
        
        return healthcareFacilities.map(facility => ({
            value: facility.id.toString(),
            label: facility.name,
        }));
    }, [healthcareFacilities, facilitiesError]);

    return (
        <Paper p="md" radius="md" shadow="sm">
            <Stack gap="md">
                <Group grow>
                    <Select
                        label={t('billing.filters.batchStatus.label')}
                        placeholder={t('billing.filters.batchStatus.placeholder')}
                        leftSection={<IconFilter size={16} />}
                        data={batchStatusOptions}
                        value={filters.batchStatus}
                        onChange={handleBatchStatusChange}
                        disabled={isLoading}
                    />
                    
                    <Select
                        label={t('billing.filters.orderStatus.label')}
                        placeholder={t('billing.filters.orderStatus.placeholder')}
                        leftSection={<IconFilter size={16} />}
                        data={orderStatusOptions}
                        value={filters.orderStatus}
                        onChange={handleOrderStatusChange}
                        disabled={isLoading}
                    />
                    
                    <CalendarDateInput
                        label={t('billing.filters.fromDate.label')}
                        placeholder={t('billing.filters.fromDate.placeholder')}
                        leftSection={<IconCalendar size={16} />}
                        valueFormat="YYYY-MM-DD"
                        value={filters.fromDate}
                        onChange={handleFromDateChange}
                        clearable
                    />
                    
                    <CalendarDateInput
                        label={t('billing.filters.toDate.label')}
                        placeholder={t('billing.filters.toDate.placeholder')}
                        leftSection={<IconCalendar size={16} />}
                        valueFormat="YYYY-MM-DD"
                        value={filters.toDate}
                        onChange={handleToDateChange}
                        clearable
                    />
                </Group>
                
                <Group grow>
                    <MultiSelect
                        label={t('billing.filters.facilities.label')}
                        placeholder={
                            facilitiesLoading 
                                ? t('billing.filters.facilities.loading')
                                : facilitiesError 
                                    ? t('billing.filters.facilities.error')
                                    : t('billing.filters.facilities.placeholder')
                        }
                        leftSection={<IconBuildingHospital size={16} />}
                        data={facilityOptions}
                        value={filters.facilities}
                        onChange={handleFacilitiesChange}
                        clearable
                        searchable
                        maxDropdownHeight={200}
                        comboboxProps={{ withinPortal: false }}
                        disabled={isLoading || facilitiesLoading || !!facilitiesError}
                        error={facilitiesError ? t('billing.filters.facilities.errorMessage') : undefined}
                    />
                </Group>
            </Stack>
        </Paper>
    );
};

export default BillingFilters; 